// biome-ignore-all lint/suspicious/noArrayIndexKey: the fixed 24-hour / 7-day grid lines are identified by their index by design (stable, never reordered).
// =============================================================================
// CalendarPage.jsx — Resources → Calendar (Zephyr staff roster / availability)
// =============================================================================
// Replaces the old Google Sites roster. Week + Month views over calendar_events
// (db-108). Every internal user may VIEW; create / edit / delete is gated on the
// matrix "Calendar Management" module (SuperUser always; a reduced Admin only when
// granted — John, 26 Jul 2026) and enforced by RLS. Events are coloured by their
// assigned Zephyr Staff member (profiles.staff_colour), or a manual colour when
// staff-less. Date / recurrence / layout maths lives in src/services/calendar.ts.
// =============================================================================

import React from "react";
import { canAccess } from "../../app/access";
import {
  createCalendarEvent,
  deactivateCalendarEvent,
  endCalendarSeriesBefore,
  listCalendarEventExceptions,
  listCalendarEvents,
  updateCalendarEvent,
  upsertCalendarEventException,
} from "../../data/calendarEvents";
import { listStaffProfiles } from "../../data/profiles";
import {
  addDays,
  DEFAULT_EVENT_COLOUR,
  eventColour,
  expandEvents,
  formatMonthTitle,
  formatTime,
  formatWeekTitle,
  isoToLocalDate,
  isoToLocalTime,
  layoutDayOccurrences,
  localToIso,
  monthGridDays,
  occursOnDay,
  sameDay,
  startOfDay,
  startOfMonth,
  startOfWeek,
  WEEKDAY_LABELS,
  weekDays,
} from "../../services/calendar";
import { logAudit } from "../../utils/auditLog";
import { ModalBackdrop } from "../form/FormUI.jsx";

const HOUR_PX = 44;
const DAY_PX = 24 * HOUR_PX;
const COLOUR_PRESETS = [
  "#5C6BC0",
  "#00897B",
  "#E53935",
  "#8E24AA",
  "#43A047",
  "#FB8C00",
  "#3949AB",
  "#6D4C41",
  "#546E7A",
  "#C0CA33",
];

function staffName(p) {
  return (
    [p.staff_first_name, p.staff_last_name].filter(Boolean).join(" ") ||
    p.display_name ||
    p.staff_user_email ||
    "Staff"
  );
}

// White or near-black text depending on the background, for legibility.
function textOn(hex) {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex || "");
  if (!m) return "#FFFFFF";
  const n = parseInt(m[1], 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return lum > 0.62 ? "#1A2E2B" : "#FFFFFF";
}

export function CalendarPage({ user }) {
  // Create/edit/delete is gated on the matrix "Calendar Management" module
  // (SuperUser always; a reduced Admin only when granted). Viewing is open to all
  // internal staff. (John, 26 Jul 2026 — was blanket isAdminRole.)
  const canEdit = canAccess(user, "calendar_manage");
  const [events, setEvents] = React.useState([]);
  const [exceptions, setExceptions] = React.useState([]);
  const [staff, setStaff] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [viewDate, setViewDate] = React.useState(function () {
    return startOfDay(new Date());
  });
  const [mode, setMode] = React.useState("week");
  const [selected, setSelected] = React.useState(null); // an Occurrence
  const [editing, setEditing] = React.useState(null); // a form model or null
  const [scopePrompt, setScopePrompt] = React.useState(null); // { mode, occ } or null
  const [deleteErr, setDeleteErr] = React.useState("");

  function reload() {
    return Promise.all([listCalendarEvents(), listCalendarEventExceptions()]).then(function (res) {
      setEvents(res[0] || []);
      setExceptions(res[1] || []);
    });
  }

  React.useEffect(function () {
    Promise.all([listCalendarEvents(), listStaffProfiles(), listCalendarEventExceptions()]).then(
      function (res) {
        setEvents(res[0] || []);
        setStaff((res[1] || []).filter((p) => !p.deactivated));
        setExceptions(res[2] || []);
        setLoading(false);
      }
    );
  }, []);

  const staffById = React.useMemo(
    function () {
      const m = {};
      for (const p of staff) m[p.id] = p;
      return m;
    },
    [staff]
  );
  const staffColourById = React.useMemo(
    function () {
      const m = {};
      for (const p of staff) m[p.id] = p.staff_colour;
      return m;
    },
    [staff]
  );

  // The visible date range + expanded occurrences.
  const range = React.useMemo(
    function () {
      if (mode === "week") {
        const s = startOfWeek(viewDate);
        return { start: s, end: addDays(s, 7), days: weekDays(viewDate) };
      }
      const grid = monthGridDays(viewDate);
      return { start: grid[0], end: addDays(grid[41], 1), days: grid };
    },
    [mode, viewDate]
  );
  const occurrences = React.useMemo(
    function () {
      return expandEvents(events, range.start, range.end, exceptions);
    },
    [events, range, exceptions]
  );

  function go(delta) {
    setViewDate(function (d) {
      if (mode === "week") return addDays(d, 7 * delta);
      const n = startOfMonth(d);
      n.setMonth(n.getMonth() + delta);
      return n;
    });
  }

  function openNew() {
    const today = isoToLocalDate(new Date().toISOString());
    setEditing({
      id: null,
      title: "",
      staff_id: "",
      color: DEFAULT_EVENT_COLOUR,
      all_day: false,
      startDate: today,
      startTime: "09:00",
      endDate: today,
      endTime: "13:00",
      recurrence: "none",
      recur_until: "",
      description: "",
    });
  }

  // Edit request from the details modal: a recurring event asks for a scope
  // first (This / This & following / All); a one-off opens straight to the form.
  function requestEdit(occ) {
    if (occ.event.recurrence === "weekly") {
      setSelected(null);
      setScopePrompt({ mode: "edit", occ });
    } else {
      openEditScoped(occ, "all");
    }
  }
  function requestDelete(occ) {
    if (occ.event.recurrence === "weekly") {
      setSelected(null);
      setScopePrompt({ mode: "delete", occ });
    } else {
      doDelete(occ, "all");
    }
  }

  // Open the form prefilled from the CLICKED occurrence (not the series base), so
  // a "this event" edit shows that instance's own date/time. `scope` + the
  // occurrence ride along on the model so save() can route (see EventForm.save).
  function openEditScoped(occ, scope) {
    const ev = occ.event;
    const allDay = ev.all_day;
    // Storage end is exclusive for all-day → show the inclusive last day.
    const endIso = allDay ? addDays(new Date(occ.end), -1).toISOString() : occ.end.toISOString();
    setScopePrompt(null);
    setSelected(null);
    setEditing({
      id: ev.id,
      title: ev.title,
      staff_id: ev.staff_id || "",
      color: ev.color || DEFAULT_EVENT_COLOUR,
      all_day: allDay,
      startDate: isoToLocalDate(occ.start.toISOString()),
      startTime: isoToLocalTime(occ.start.toISOString()),
      endDate: isoToLocalDate(endIso),
      endTime: isoToLocalTime(occ.end.toISOString()),
      recurrence: ev.recurrence || "none",
      recur_until: ev.recur_until || "",
      description: ev.description || "",
      _scope: scope,
      _occ: occ,
    });
  }

  function doDelete(occ, scope) {
    const id = occ.event.id;
    let op;
    if (scope === "this") {
      op = upsertCalendarEventException({
        event_id: id,
        occurrence_date: occ.occurrenceDate,
        cancelled: true,
      });
    } else if (scope === "following") {
      op = endCalendarSeriesBefore(id, occ.occurrenceDate);
    } else {
      op = deactivateCalendarEvent(id);
    }
    // Check r.error before declaring success — the data functions RESOLVE { error }
    // rather than throwing, so a bare .then() treated an RLS denial as a delete: the
    // panel closed, a calendar_event_deleted audit row was written, and the event came
    // straight back on reload. Same shape the save path already uses (see EventForm's
    // op.then below). Finding B4, 18 Aug 2026.
    op.then(function (r) {
      if (r?.error) {
        setDeleteErr(`Delete failed: ${r.error.message}`);
        return;
      }
      setDeleteErr("");
      logAudit("calendar_event_deleted", { id, scope, by: user?.shelterName });
      setScopePrompt(null);
      setSelected(null);
      reload();
    });
  }

  if (loading) return <div className="text-zephyr-muted p-6">Loading…</div>;

  const title = mode === "week" ? formatWeekTitle(viewDate) : formatMonthTitle(viewDate);

  return (
    <div className="p-6 max-w-[var(--page-max)] mx-auto">
      {deleteErr && (
        <div className="text-[13px] text-[#C62828] font-semibold mb-3">{deleteErr}</div>
      )}
      {/* Header */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <h1 className="m-0 text-[22px] text-zephyr-text">Calendar</h1>
        <button
          type="button"
          onClick={function () {
            setViewDate(startOfDay(new Date()));
          }}
          className="border border-zephyr-border rounded-[8px] px-3 py-[6px] text-[13px] bg-white cursor-pointer text-zephyr-text"
          style={{ fontFamily: "inherit" }}
        >
          Today
        </button>
        <div className="flex items-center gap-1">
          <button
            type="button"
            aria-label="Previous"
            onClick={function () {
              go(-1);
            }}
            className="border border-zephyr-border rounded-[8px] w-8 h-8 bg-white cursor-pointer text-zephyr-text"
          >
            ‹
          </button>
          <button
            type="button"
            aria-label="Next"
            onClick={function () {
              go(1);
            }}
            className="border border-zephyr-border rounded-[8px] w-8 h-8 bg-white cursor-pointer text-zephyr-text"
          >
            ›
          </button>
        </div>
        <div className="text-[16px] font-bold text-zephyr-text">{title}</div>
        <div className="ml-auto flex items-center gap-2">
          <div className="inline-flex rounded-[8px] overflow-hidden border border-zephyr-border">
            {["week", "month"].map(function (m) {
              return (
                <button
                  key={m}
                  type="button"
                  onClick={function () {
                    setMode(m);
                  }}
                  className={`px-3 py-[6px] text-[13px] cursor-pointer ${
                    mode === m ? "bg-zephyr-teal text-white font-bold" : "bg-white text-zephyr-text"
                  }`}
                  style={{ fontFamily: "inherit", border: "none" }}
                >
                  {m === "week" ? "Week" : "Month"}
                </button>
              );
            })}
          </div>
          {canEdit && (
            <button
              type="button"
              onClick={openNew}
              className="bg-zephyr-teal text-white rounded-[8px] px-4 py-[7px] text-[13px] font-bold cursor-pointer"
              style={{ fontFamily: "inherit", border: "none" }}
            >
              + Create
            </button>
          )}
        </div>
      </div>

      {mode === "week" ? (
        <WeekView
          days={range.days}
          occurrences={occurrences}
          staffColourById={staffColourById}
          onSelect={setSelected}
        />
      ) : (
        <MonthView
          days={range.days}
          viewDate={viewDate}
          occurrences={occurrences}
          staffColourById={staffColourById}
          onSelect={setSelected}
        />
      )}

      {selected && (
        <EventDetails
          occ={selected}
          staffById={staffById}
          canEdit={canEdit}
          onClose={function () {
            setSelected(null);
          }}
          onEdit={function () {
            requestEdit(selected);
          }}
          onDelete={function () {
            requestDelete(selected);
          }}
        />
      )}

      {scopePrompt && (
        <ScopeChooser
          promptMode={scopePrompt.mode}
          onClose={function () {
            setScopePrompt(null);
          }}
          onPick={function (scope) {
            if (scopePrompt.mode === "delete") doDelete(scopePrompt.occ, scope);
            else openEditScoped(scopePrompt.occ, scope);
          }}
        />
      )}

      {editing && (
        <EventForm
          model={editing}
          staff={staff}
          onCancel={function () {
            setEditing(null);
          }}
          onSaved={function () {
            setEditing(null);
            reload();
          }}
          user={user}
        />
      )}
    </div>
  );
}

// ── Week view ────────────────────────────────────────────────────────────────
function WeekView({ days, occurrences, staffColourById, onSelect }) {
  const allDay = occurrences.filter((o) => o.allDay);
  const timed = occurrences.filter((o) => !o.allDay);
  const gutter = 56;
  const scrollRef = React.useRef(null);
  // Open scrolled to ~7am so the working day is in view.
  React.useEffect(function () {
    if (scrollRef.current) scrollRef.current.scrollTop = 7 * HOUR_PX;
  }, []);

  return (
    <div className="border border-zephyr-border rounded-[10px] overflow-hidden bg-white">
      {/* Day headings */}
      <div className="grid" style={{ gridTemplateColumns: `${gutter}px repeat(7, 1fr)` }}>
        <div className="border-b border-zephyr-border" />
        {days.map(function (d) {
          const today = sameDay(d, new Date());
          return (
            <div
              key={d.toISOString()}
              className="text-center py-2 border-b border-l border-zephyr-border"
            >
              <div className="text-[11px] uppercase text-zephyr-muted">
                {WEEKDAY_LABELS[d.getDay()]}
              </div>
              <div
                className={`text-[16px] ${today ? "font-bold text-zephyr-teal" : "text-zephyr-text"}`}
              >
                {d.getDate()}
              </div>
            </div>
          );
        })}
      </div>

      {/* All-day band */}
      {allDay.length > 0 && (
        <div
          className="grid border-b border-zephyr-border"
          style={{ gridTemplateColumns: `${gutter}px repeat(7, 1fr)` }}
        >
          <div className="text-[10px] text-zephyr-muted py-1 pr-1 text-right">all-day</div>
          <div
            className="col-span-7 grid gap-[2px] py-1"
            style={{ gridTemplateColumns: "repeat(7, 1fr)" }}
          >
            {allDay.map(function (o) {
              const first = days.findIndex((d) => occursOnDay(o, d));
              let last = first;
              for (let i = days.length - 1; i >= 0; i--) {
                if (occursOnDay(o, days[i])) {
                  last = i;
                  break;
                }
              }
              if (first < 0) return null;
              const bg = eventColour(o.event, staffColourById);
              return (
                <button
                  key={o.event.id + o.start.toISOString()}
                  type="button"
                  onClick={function () {
                    onSelect(o);
                  }}
                  title={o.event.title}
                  className="text-left text-[11px] font-semibold rounded px-2 py-1 overflow-hidden whitespace-nowrap text-ellipsis cursor-pointer"
                  style={{
                    gridColumn: `${first + 1} / ${last + 2}`,
                    background: bg,
                    color: textOn(bg),
                    border: "none",
                  }}
                >
                  {o.event.title}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Timed grid */}
      <div ref={scrollRef} style={{ maxHeight: 560, overflowY: "auto" }}>
        <div
          className="grid relative"
          style={{ gridTemplateColumns: `${gutter}px repeat(7, 1fr)` }}
        >
          {/* Hour gutter */}
          <div style={{ height: DAY_PX, position: "relative" }}>
            {Array.from({ length: 24 }, (_, h) => (
              <div
                key={h}
                className="text-[10px] text-zephyr-muted text-right pr-1"
                style={{ position: "absolute", top: h * HOUR_PX - 6, right: 0, width: gutter }}
              >
                {h === 0 ? "" : formatTime(new Date(2000, 0, 1, h, 0))}
              </div>
            ))}
          </div>
          {/* Day columns */}
          {days.map(function (d) {
            const laid = layoutDayOccurrences(timed, d);
            return (
              <div
                key={d.toISOString()}
                className="border-l border-zephyr-border relative"
                style={{ height: DAY_PX }}
              >
                {Array.from({ length: 24 }, (_, h) => (
                  <div
                    key={h}
                    style={{
                      position: "absolute",
                      top: h * HOUR_PX,
                      left: 0,
                      right: 0,
                      borderTop: "1px solid #EEF2F1",
                    }}
                  />
                ))}
                {laid.map(function (item) {
                  const o = item.occ;
                  const bg = eventColour(o.event, staffColourById);
                  return (
                    <button
                      key={o.event.id + o.start.toISOString()}
                      type="button"
                      onClick={function () {
                        onSelect(o);
                      }}
                      className="absolute text-left rounded px-2 py-1 overflow-hidden cursor-pointer flex flex-col items-stretch justify-start"
                      style={{
                        top: item.topFraction * DAY_PX + 1,
                        height: Math.max(18, item.heightFraction * DAY_PX - 2),
                        left: `calc(${item.leftFraction * 100}% + 1px)`,
                        width: `calc(${item.widthFraction * 100}% - 2px)`,
                        background: bg,
                        color: textOn(bg),
                        border: "none",
                      }}
                    >
                      <div className="text-[11px] font-bold leading-tight overflow-hidden break-words">
                        {o.event.title}
                      </div>
                      <div className="text-[10px] opacity-90">
                        {formatTime(o.start)} – {formatTime(o.end)}
                      </div>
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Month view ───────────────────────────────────────────────────────────────
function MonthView({ days, viewDate, occurrences, staffColourById, onSelect }) {
  const month = viewDate.getMonth();
  const MAX = 3;
  return (
    <div className="border border-zephyr-border rounded-[10px] overflow-hidden bg-white">
      <div className="grid" style={{ gridTemplateColumns: "repeat(7, 1fr)" }}>
        {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map(function (w) {
          return (
            <div
              key={w}
              className="text-center py-2 text-[11px] uppercase text-zephyr-muted border-b border-zephyr-border"
            >
              {w}
            </div>
          );
        })}
      </div>
      <div className="grid" style={{ gridTemplateColumns: "repeat(7, 1fr)" }}>
        {days.map(function (d) {
          const dayOccs = occurrences.filter((o) => occursOnDay(o, d));
          const inMonth = d.getMonth() === month;
          const today = sameDay(d, new Date());
          return (
            <div
              key={d.toISOString()}
              className="border-l border-b border-zephyr-border p-1 align-top"
              style={{ minHeight: 104, background: inMonth ? "#FFFFFF" : "#F7F9F9" }}
            >
              <div
                className={`text-[12px] mb-1 ${
                  today
                    ? "font-bold text-zephyr-teal"
                    : inMonth
                      ? "text-zephyr-text"
                      : "text-zephyr-muted"
                }`}
              >
                {d.getDate()}
              </div>
              {dayOccs.slice(0, MAX).map(function (o) {
                const bg = eventColour(o.event, staffColourById);
                return (
                  <button
                    key={o.event.id + o.start.toISOString()}
                    type="button"
                    onClick={function () {
                      onSelect(o);
                    }}
                    title={o.event.title}
                    className="block w-full text-left text-[10px] font-semibold rounded px-1 py-[2px] mb-[2px] overflow-hidden whitespace-nowrap text-ellipsis cursor-pointer"
                    style={{ background: bg, color: textOn(bg), border: "none" }}
                  >
                    {o.allDay ? "" : `${formatTime(o.start)} `}
                    {o.event.title}
                  </button>
                );
              })}
              {dayOccs.length > MAX && (
                <div className="text-[10px] text-zephyr-muted">+{dayOccs.length - MAX} more</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Event details (click) ────────────────────────────────────────────────────
function EventDetails({ occ, staffById, canEdit, onClose, onEdit, onDelete }) {
  const ev = occ.event;
  const owner = ev.staff_id ? staffById[ev.staff_id] : null;
  const when = occ.allDay
    ? `${occ.start.toLocaleDateString("en-AU", { weekday: "long", day: "numeric", month: "long" })}${
        sameDay(occ.start, addDays(occ.end, -1))
          ? ""
          : ` – ${addDays(occ.end, -1).toLocaleDateString("en-AU", { day: "numeric", month: "long" })}`
      } · All day`
    : `${occ.start.toLocaleDateString("en-AU", {
        weekday: "long",
        day: "numeric",
        month: "long",
      })} · ${formatTime(occ.start)} – ${formatTime(occ.end)}`;
  return (
    <ModalBackdrop
      portal
      onClose={onClose}
      contentStyle={{
        background: "white",
        borderRadius: 12,
        padding: 24,
        width: "calc(100vw - 40px)",
        maxWidth: 440,
        boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
      }}
    >
      <div className="flex justify-between items-start gap-3">
        <h2 className="m-0 text-[18px] text-zephyr-text">{ev.title}</h2>
        <button
          type="button"
          onClick={onClose}
          className="bg-transparent border-none text-[18px] cursor-pointer text-zephyr-muted"
        >
          ✕
        </button>
      </div>
      <div className="text-[13px] text-zephyr-muted mt-2">{when}</div>
      {ev.recurrence === "weekly" && (
        <div className="text-[12px] text-zephyr-muted mt-1">Repeats weekly</div>
      )}
      {owner && (
        <div className="text-[13px] text-zephyr-text mt-2">
          <strong>Staff:</strong> {staffName(owner)}
        </div>
      )}
      {ev.description && (
        <div className="text-[13px] text-zephyr-text mt-2 whitespace-pre-wrap">
          {ev.description}
        </div>
      )}
      {canEdit && (
        <div className="flex gap-2 mt-5">
          <button
            type="button"
            onClick={onEdit}
            className="bg-zephyr-teal text-white rounded-[8px] px-4 py-[7px] text-[13px] font-bold cursor-pointer border-none"
          >
            Edit
          </button>
          <button
            type="button"
            onClick={onDelete}
            className="bg-white text-[#C62828] rounded-[8px] px-4 py-[7px] text-[13px] font-bold cursor-pointer"
            style={{ border: "1px solid #E0A0A0" }}
          >
            Delete
          </button>
        </div>
      )}
    </ModalBackdrop>
  );
}

// ── Recurring edit/delete scope chooser ──────────────────────────────────────
function ScopeChooser({ promptMode, onPick, onClose }) {
  const verb = promptMode === "delete" ? "Delete" : "Edit";
  const opt =
    "block w-full text-left rounded-[8px] px-4 py-3 text-[14px] cursor-pointer bg-white text-zephyr-text mb-2";
  return (
    <ModalBackdrop
      portal
      onClose={onClose}
      contentStyle={{
        background: "white",
        borderRadius: 12,
        padding: 24,
        width: "calc(100vw - 40px)",
        maxWidth: 420,
        boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
      }}
    >
      <h2 className="m-0 mb-1 text-[18px] text-zephyr-text">{verb} recurring event</h2>
      <p className="text-[13px] text-zephyr-muted mt-0 mb-4">
        This appointment repeats weekly. Which events should this {verb.toLowerCase()} affect?
      </p>
      <button
        type="button"
        onClick={() => onPick("this")}
        className={opt}
        style={{ border: "1px solid #CFE0DE" }}
      >
        <strong>This event only</strong>
        <div className="text-[12px] text-zephyr-muted">
          Just this one date; other weeks unchanged.
        </div>
      </button>
      <button
        type="button"
        onClick={() => onPick("following")}
        className={opt}
        style={{ border: "1px solid #CFE0DE" }}
      >
        <strong>This and following events</strong>
        <div className="text-[12px] text-zephyr-muted">This date and every week after it.</div>
      </button>
      <button
        type="button"
        onClick={() => onPick("all")}
        className={opt}
        style={{ border: "1px solid #CFE0DE" }}
      >
        <strong>All events</strong>
        <div className="text-[12px] text-zephyr-muted">The whole series, past and future.</div>
      </button>
      <div className="flex justify-end mt-2">
        <button
          type="button"
          onClick={onClose}
          className="bg-white text-zephyr-text rounded-[8px] px-4 py-[7px] text-[13px] font-semibold cursor-pointer"
          style={{ border: "1px solid #CFE0DE" }}
        >
          Cancel
        </button>
      </div>
    </ModalBackdrop>
  );
}

// ── Create / edit form ───────────────────────────────────────────────────────
function EventForm({ model, staff, onCancel, onSaved, user }) {
  const [m, setM] = React.useState(model);
  const [saving, setSaving] = React.useState(false);
  const [err, setErr] = React.useState("");
  function set(k, v) {
    setM(function (prev) {
      return { ...prev, [k]: v };
    });
  }

  function save() {
    if (!m.title.trim()) {
      setErr("Please enter a title.");
      return;
    }
    let startsAt;
    let endsAt;
    if (m.all_day) {
      const end = m.endDate || m.startDate;
      startsAt = localToIso(m.startDate, "00:00");
      // Exclusive end: midnight after the last inclusive day.
      endsAt = localToIso(
        isoToLocalDate(addDays(new Date(localToIso(end, "00:00")), 1).toISOString()),
        "00:00"
      );
    } else {
      startsAt = localToIso(m.startDate, m.startTime);
      endsAt = localToIso(m.endDate || m.startDate, m.endTime);
    }
    if (new Date(endsAt).getTime() <= new Date(startsAt).getTime()) {
      setErr("The end must be after the start.");
      return;
    }
    const row = {
      title: m.title.trim(),
      staff_id: m.staff_id || null,
      color: m.staff_id ? null : m.color || DEFAULT_EVENT_COLOUR,
      all_day: !!m.all_day,
      starts_at: startsAt,
      ends_at: endsAt,
      recurrence: m.recurrence === "weekly" ? "weekly" : "none",
      recur_until: m.recurrence === "weekly" && m.recur_until ? m.recur_until : null,
      description: m.description ? m.description.trim() : null,
    };
    setSaving(true);
    setErr("");

    // Route the save by edit scope (recurring only; new + one-off use "all").
    let op;
    if (!m.id) {
      op = createCalendarEvent({ ...row, created_by: user?.id || null });
    } else if (m._scope === "this") {
      // Single-instance override → an exception row anchored to the original date.
      op = upsertCalendarEventException({
        event_id: m.id,
        occurrence_date: m._occ.occurrenceDate,
        cancelled: false,
        title: row.title,
        staff_id: row.staff_id,
        color: row.color,
        all_day: row.all_day,
        starts_at: row.starts_at,
        ends_at: row.ends_at,
        description: row.description,
      });
    } else if (m._scope === "following") {
      // End the old series the day before, start a new one from this instance.
      op = endCalendarSeriesBefore(m.id, m._occ.occurrenceDate).then(function (r) {
        if (r.error) return r;
        return createCalendarEvent({ ...row, created_by: user?.id || null });
      });
    } else {
      op = updateCalendarEvent(m.id, row);
    }

    op.then(function (r) {
      setSaving(false);
      if (r.error) {
        setErr(`Save failed: ${r.error.message}`);
        return;
      }
      logAudit(m.id ? "calendar_event_updated" : "calendar_event_created", {
        id: m.id || undefined,
        title: row.title,
        scope: m.id ? m._scope || "all" : undefined,
        by: user?.shelterName,
      });
      onSaved();
    });
  }

  const label = "text-[12px] font-bold text-zephyr-muted uppercase tracking-[0.4px] block mb-1";
  const field =
    "w-full py-[8px] px-[10px] rounded-[8px] border border-zephyr-border text-[14px] box-border text-zephyr-text bg-white";

  return (
    <ModalBackdrop
      portal
      onClose={onCancel}
      contentStyle={{
        background: "white",
        borderRadius: 12,
        padding: 24,
        width: "calc(100vw - 40px)",
        maxWidth: 520,
        maxHeight: "85vh",
        overflowY: "auto",
        boxShadow: "0 8px 32px rgba(0,0,0,0.25)",
      }}
    >
      <h2 className="m-0 mb-4 text-[18px] text-zephyr-text">
        {!m.id
          ? "New event"
          : m._scope === "this"
            ? "Edit this occurrence"
            : m._scope === "following"
              ? "Edit this & following"
              : "Edit event"}
      </h2>

      <label htmlFor="cal-title" className={label}>
        Title
      </label>
      <input
        id="cal-title"
        type="text"
        value={m.title}
        onChange={(e) => set("title", e.target.value)}
        placeholder="Weekly Roster : NAME"
        className={`${field} mb-3`}
      />

      <label htmlFor="cal-staff" className={label}>
        Staff member (colour comes from them)
      </label>
      <select
        id="cal-staff"
        value={m.staff_id}
        onChange={(e) => set("staff_id", e.target.value)}
        className={`${field} mb-3`}
      >
        <option value="">— None (pick a colour) —</option>
        {staff.map((p) => (
          <option key={p.id} value={p.id}>
            {staffName(p)}
          </option>
        ))}
      </select>

      {!m.staff_id && (
        <div className="mb-3">
          <div className={label}>Colour</div>
          <div className="flex gap-2 flex-wrap">
            {COLOUR_PRESETS.map((c) => (
              <button
                key={c}
                type="button"
                aria-label={`colour ${c}`}
                onClick={() => set("color", c)}
                style={{
                  width: 26,
                  height: 26,
                  borderRadius: "50%",
                  background: c,
                  border: m.color === c ? "3px solid #1A2E2B" : "2px solid #FFFFFF",
                  boxShadow: "0 0 0 1px #CFE0DE",
                  cursor: "pointer",
                }}
              />
            ))}
          </div>
        </div>
      )}

      <label className="flex items-center gap-2 mb-3 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={m.all_day}
          onChange={(e) => set("all_day", e.target.checked)}
          className="w-[16px] h-[16px]"
        />
        <span className="text-[14px] text-zephyr-text">All day</span>
      </label>

      <div className="grid gap-3 mb-3" style={{ gridTemplateColumns: "1fr 1fr" }}>
        <div>
          <label htmlFor="cal-sd" className={label}>
            Start date
          </label>
          <input
            id="cal-sd"
            type="date"
            value={m.startDate}
            onChange={(e) => set("startDate", e.target.value)}
            className={field}
          />
        </div>
        {!m.all_day && (
          <div>
            <label htmlFor="cal-st" className={label}>
              Start time
            </label>
            <input
              id="cal-st"
              type="time"
              value={m.startTime}
              onChange={(e) => set("startTime", e.target.value)}
              className={field}
            />
          </div>
        )}
        <div>
          <label htmlFor="cal-ed" className={label}>
            End date
          </label>
          <input
            id="cal-ed"
            type="date"
            value={m.endDate}
            onChange={(e) => set("endDate", e.target.value)}
            className={field}
          />
        </div>
        {!m.all_day && (
          <div>
            <label htmlFor="cal-et" className={label}>
              End time
            </label>
            <input
              id="cal-et"
              type="time"
              value={m.endTime}
              onChange={(e) => set("endTime", e.target.value)}
              className={field}
            />
          </div>
        )}
      </div>

      {/* A single-instance edit ("this event only") can't change the repeat rule. */}
      {m._scope !== "this" && (
        <>
          <label htmlFor="cal-rec" className={label}>
            Repeat
          </label>
          <select
            id="cal-rec"
            value={m.recurrence}
            onChange={(e) => set("recurrence", e.target.value)}
            className={`${field} mb-3`}
          >
            <option value="none">Does not repeat</option>
            <option value="weekly">Weekly</option>
          </select>

          {m.recurrence === "weekly" && (
            <div className="mb-3">
              <label htmlFor="cal-until" className={label}>
                Repeat until (optional)
              </label>
              <input
                id="cal-until"
                type="date"
                value={m.recur_until}
                onChange={(e) => set("recur_until", e.target.value)}
                className={field}
                style={{ maxWidth: 200 }}
              />
            </div>
          )}
        </>
      )}

      <label htmlFor="cal-desc" className={label}>
        Description (optional)
      </label>
      <textarea
        id="cal-desc"
        value={m.description}
        onChange={(e) => set("description", e.target.value)}
        rows={3}
        className={`${field} mb-3`}
        style={{ fontFamily: "inherit" }}
      />

      {err && <div className="text-[13px] text-[#C62828] font-semibold mb-3">{err}</div>}

      <div className="flex gap-2 justify-end">
        <button
          type="button"
          onClick={onCancel}
          className="bg-white text-zephyr-text rounded-[8px] px-4 py-[7px] text-[13px] font-semibold cursor-pointer"
          style={{ border: "1px solid #CFE0DE" }}
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="bg-zephyr-teal text-white rounded-[8px] px-5 py-[7px] text-[13px] font-bold cursor-pointer border-none"
        >
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </ModalBackdrop>
  );
}
